import { useCallback, useEffect, useMemo } from 'react';
import {
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { spaceInboxApi } from '../services/api';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import type { PlatformEvent } from '../types/events';
import type {
  InboxItem,
  InboxItemStatus,
  InboxRefType,
} from '../types/inbox';
import type { PaginatedResponse } from '../types';

const PAGE_SIZE = 30;

export interface SpaceInboxFilter {
  spaceId: string;
  /** Slug or labelId — backend accepts either. */
  label?: string;
  status?: InboxItemStatus | 'all';
  refType?: InboxRefType;
}

type InboxPage = PaginatedResponse<InboxItem>;
type InboxQueryData = InfiniteData<InboxPage>;

const spaceInboxListKey = (f: SpaceInboxFilter) =>
  [
    'spaceInbox',
    'list',
    {
      spaceId: f.spaceId,
      label: f.label ?? null,
      status: f.status ?? 'pending',
      refType: f.refType ?? null,
    },
  ] as const;

function matchesFilter(item: InboxItem, filter: SpaceInboxFilter): boolean {
  if (item.spaceId !== filter.spaceId) return false;
  const status = filter.status ?? 'pending';
  if (status !== 'all' && item.status !== status) return false;
  if (filter.refType && item.refType !== filter.refType) return false;
  if (filter.label && !(item.labelIds ?? []).includes(filter.label)) {
    // Server filters by slug or id; we only have ids client-side so this
    // guard is best-effort for live WS adds.
    return false;
  }
  return true;
}

function removeFromPages(data: InboxQueryData | undefined, id: string): InboxQueryData | undefined {
  if (!data) return data;
  let changed = false;
  const pages = data.pages.map((page) => {
    if (!page.items.some((x) => x.inboxItemId === id)) return page;
    changed = true;
    return { ...page, items: page.items.filter((x) => x.inboxItemId !== id) };
  });
  return changed ? { ...data, pages } : data;
}

function prependItem(data: InboxQueryData | undefined, item: InboxItem): InboxQueryData | undefined {
  if (!data || data.pages.length === 0) return data;
  for (const page of data.pages) {
    if (page.items.some((x) => x.inboxItemId === item.inboxItemId)) return data;
  }
  const [first, ...rest] = data.pages;
  return { ...data, pages: [{ ...first, items: [item, ...first.items] }, ...rest] };
}

function replaceInPages(
  data: InboxQueryData | undefined,
  id: string,
  updater: (item: InboxItem) => InboxItem,
): InboxQueryData | undefined {
  if (!data) return data;
  let changed = false;
  const pages = data.pages.map((page) => {
    const idx = page.items.findIndex((x) => x.inboxItemId === id);
    if (idx === -1) return page;
    changed = true;
    const items = page.items.slice();
    items[idx] = updater(items[idx]);
    return { ...page, items };
  });
  return changed ? { ...data, pages } : data;
}

/**
 * Team-queue inbox scoped to a single space, optionally filtered server-side
 * by label slug or id. Mirrors `useInbox` (`src/hooks/useInbox.ts`) for cache
 * shape and WS handling, plus a `_updated` branch for relabel events.
 */
export function useSpaceInbox(filter: SpaceInboxFilter) {
  const queryClient = useQueryClient();
  const { subscribe, isConnected, diagnostics } = usePlatformEvents();

  const queryKey = useMemo(
    () => spaceInboxListKey(filter),
    [filter.spaceId, filter.label, filter.status, filter.refType],
  );

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useInfiniteQuery<InboxPage, Error, InboxQueryData, typeof queryKey, string | undefined>({
    queryKey,
    queryFn: ({ pageParam }) =>
      spaceInboxApi.list({
        spaceId: filter.spaceId,
        label: filter.label,
        status: filter.status ?? 'pending',
        refType: filter.refType,
        limit: PAGE_SIZE,
        cursor: pageParam,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextToken,
    staleTime: 0,
    retry: false,
    enabled: !!filter.spaceId,
  });

  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  const handleEvent = useCallback(
    (event: PlatformEvent) => {
      const type = event.eventType;
      const d = event.data as Record<string, unknown>;

      if (type === 'inbox.item_added' || type === 'inbox.item.added') {
        const item = d.item as InboxItem | undefined;
        if (!item || !matchesFilter(item, filter)) return;
        queryClient.setQueryData<InboxQueryData>(queryKey, (prev) => prependItem(prev, item));
        return;
      }

      if (type === 'inbox.item_updated' || type === 'inbox.item.updated') {
        const item = d.item as InboxItem | undefined;
        const id = item?.inboxItemId ?? (d.inboxItemId as string | undefined);
        if (!id) return;
        // If a label filter is active and the updated item no longer carries
        // the label, drop it; otherwise patch in place.
        if (filter.label && item && !matchesFilter(item, filter)) {
          queryClient.setQueryData<InboxQueryData>(queryKey, (prev) => removeFromPages(prev, id));
        } else {
          queryClient.setQueryData<InboxQueryData>(queryKey, (prev) =>
            replaceInPages(prev, id, (existing) => (item ? { ...existing, ...item } : existing)),
          );
        }
        queryClient.invalidateQueries({ queryKey: ['inbox', 'item', id] });
        return;
      }

      if (
        type === 'inbox.item_resolved' ||
        type === 'inbox.item_cancelled' ||
        type === 'inbox.item_expired' ||
        type === 'inbox.item.resolved' ||
        type === 'inbox.item.cancelled' ||
        type === 'inbox.item.expired'
      ) {
        const id = (d.inboxItemId as string) ?? '';
        if (!id) return;
        if ((filter.status ?? 'pending') !== 'all') {
          queryClient.setQueryData<InboxQueryData>(queryKey, (prev) => removeFromPages(prev, id));
        } else {
          queryClient.invalidateQueries({ queryKey });
        }
        queryClient.invalidateQueries({ queryKey: ['inbox', 'item', id] });
        return;
      }
    },
    [filter, queryClient, queryKey],
  );

  useEffect(() => {
    const unsub = subscribe([{ eventType: 'inbox.*' }], handleEvent);
    return unsub;
  }, [subscribe, handleEvent]);

  return {
    items,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    refetch,
    error,
    isConnected,
    diagnostics,
  };
}
